"use client"

import { useLayoutEffect, useRef } from "react"
import { FormikProvider, useFormik } from "formik"

import { FormTextarea } from "@/components/form-inputs"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { PromptDiff } from "@/components/ui/prompt-diff"
import { Skeleton } from "@/components/ui/skeleton"
import {
  useAcceptPromptSuggestionMutation,
  useRejectSuggestionMutation,
} from "@/hooks/mutations/conversation-quality"
import { useSuggestionInstructionsQuery } from "@/hooks/queries/conversation-quality"
import { describeWaiting } from "@/lib/conversation-quality/change-display"
import {
  applyPromptEdit,
  withReviewedInstruction,
  type PromptEditResult,
} from "@/lib/conversation-quality/suggestions/instruction"
import type { PromptEditPayload } from "@/lib/conversation-quality/suggestions/schema"
import type { QualitySuggestionsResult } from "@/server-actions/conversation-quality"

type InstructionSuggestion = Extract<
  QualitySuggestionsResult["suggestions"][number],
  { kind: "prompt_edit" }
>

/**
 * Diffed against the instructions as they are when it opens. The accept sends that text back and is
 * refused if the instructions differ by then, so what was reviewed is what gets saved.
 */
export function InstructionReviewDialog({
  suggestion,
  edit,
  open,
  onOpenChange,
}: {
  suggestion: InstructionSuggestion
  edit: PromptEditPayload
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const current = useSuggestionInstructionsQuery(suggestion.id)
  const close = () => onOpenChange(false)
  const accept = useAcceptPromptSuggestionMutation({ onAccepted: close })
  const reject = useRejectSuggestionMutation("prompt_edit", { onRejected: close })
  const isDeciding = accept.isPending || reject.isPending
  const { decline } = describeWaiting({ ...suggestion, edit })

  const formik = useFormik<InstructionValues>({
    initialValues: { instruction: edit.operation === "remove" ? "" : edit.instruction },
    onSubmit: async (values, helpers) => {
      if (current.data === undefined) return
      try {
        await accept.mutateAsync({
          input: {
            suggestionId: suggestion.id,
            instruction: values.instruction,
            baseInstructions: current.data,
          },
          setErrors: helpers.setErrors as (errors: Record<string, unknown>) => void,
        })
      } catch {
        // withQueryHandling has shown the toast and placed any field errors already.
      }
    },
  })

  const applied =
    current.data === undefined ? undefined : applyPromptEdit(current.data, withReviewedInstruction(edit, formik.values.instruction))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <form onSubmit={formik.handleSubmit}>
          <DialogHeader>
            <DialogTitle>Review instruction</DialogTitle>
            <DialogDescription>
              Suggested for {suggestion.agentName} from sessions about “{suggestion.topicLabel}” it
              didn&apos;t resolve. Accepting it saves a new version of the agent&apos;s instructions,
              which History can restore.
            </DialogDescription>
          </DialogHeader>

          <FormikProvider value={formik}>
            <div className="space-y-4 py-4">
              {edit.operation !== "append" && (
                <TargetLine
                  label={edit.operation === "replace" ? "Replaces this line" : "Removes this line"}
                  text={edit.target}
                />
              )}
              {edit.operation !== "remove" && (
                <FormTextarea
                  name="instruction"
                  label={edit.operation === "replace" ? "New line" : "Instruction"}
                  rows={4}
                />
              )}
              <InstructionsDiff instructions={current.data} error={current.error} applied={applied} />
            </div>
          </FormikProvider>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => reject.mutate({ input: { suggestionId: suggestion.id } })}
              disabled={isDeciding}
            >
              {decline}
            </Button>
            <Button
              type="submit"
              disabled={isDeciding || current.isFetching || !applied?.ok}
            >
              {ACCEPT_LABEL[edit.operation]}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const ACCEPT_LABEL: Record<PromptEditPayload["operation"], string> = {
  append: "Add to instructions",
  replace: "Replace in instructions",
  remove: "Remove from instructions",
}

function TargetLine({ label, text }: { label: string; text: string }) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium">{label}</p>
      <p className="whitespace-pre-wrap break-words rounded-lg border bg-muted/30 p-3 text-sm">{text}</p>
    </div>
  )
}

function InstructionsDiff({
  instructions,
  error,
  applied,
}: {
  instructions: string | undefined
  error: Error | null
  applied: PromptEditResult | undefined
}) {
  const scrollBox = useRef<HTMLDivElement>(null)
  const next = applied?.ok ? applied.instructions : undefined

  // The change can sit anywhere in a long prompt.
  useLayoutEffect(() => {
    const box = scrollBox.current
    const change = box?.querySelector('[data-diff="remove"], [data-diff="add"]')
    if (!box || !change) return
    box.scrollTop += change.getBoundingClientRect().top - box.getBoundingClientRect().top
  }, [next])

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    )
  }
  if (instructions === undefined || applied === undefined) return <Skeleton className="h-32 w-full" />
  if (!applied.ok) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          This line is no longer in the agent&apos;s instructions as it was when the suggestion
          was written. Decline it, or edit the instructions yourself.
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div ref={scrollBox} className="max-h-72 overflow-y-auto rounded-lg border bg-muted/30 p-3">
      <PromptDiff
        prev={instructions}
        next={applied.instructions}
        labels={{ prev: "current instructions", next: "with this change" }}
      />
    </div>
  )
}

interface InstructionValues {
  instruction: string;
}
